import { observer } from 'src/utils/mobx-react';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import React, { ChangeEvent, useState } from 'react';
import { action, makeAutoObservable } from 'mobx';
import { useAsObservableSource } from 'mobx-react-lite';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

interface InnerTag {
    key: string;
    label: string;
    values: Array<{
        id: string;
        isDefault: boolean;
        label: string;
    }>;
}

class TagSelectElementState {
    constructor(readonly props: TagSelectElementPropsType) {
        makeAutoObservable(this);
    }

    @action public onChangeHandler = (event: ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        this.props.eventTagState.onTagChange(value, this.props.tag);
    };
}

type TagSelectElementPropsType = {
    tag: InnerTag;
    eventId: EventId;
    eventTagState: EventTagsState;
};

const TagSelectElement = observer('TagSelectElement', (propsIn: TagSelectElementPropsType) => {
    const props = useAsObservableSource(propsIn);
    const [state] = useState(() => new TagSelectElementState(props));

    const event = props.eventId.getEventModel();

    if (event === null) {
        return null;
    }

    const currentSelected = event.getTag(props.tag.key);
    const tagName = 'tags.' + props.tag.key + '.0';

    return (
        <div className='element'>
            <div className='select-wrapper'>
                <select
                    name={tagName}
                    defaultValue={currentSelected}
                    onChange={state.onChangeHandler}
                >
                    <option />
                    {props.tag.values.map((value) => (
                        <option
                            key={value.id}
                            value={value.id}
                        >
                            {value.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
});

class EventTagsState {
    constructor(
        readonly appState: AppState,
        readonly eventId: () => EventId | null
    ) {
        makeAutoObservable(this);
    }

    public getEventTags = (): Array<InnerTag> => {
        const eventId = this.eventId();
        if (eventId === null) {
            return [];
        }
        const event = eventId.getEventModel();

        if (event === null) {
            return [];
        }

        const tags = this.appState.sdkStaff.platformTags.get();
        if (tags.type === 'ready') {
            const filteredTags: Array<InnerTag> = [];

            for (const [key, value] of Object.entries(tags.value.definitions)) {
                if (value.applicableTo.includes('event') && value.sports.includes(event.sport)) {
                    filteredTags.push({
                        key,
                        label: value.label,
                        values: value.values,
                    });
                }
            }

            filteredTags.sort((prevTag, nextTag) => (prevTag.key < nextTag.key ? -1 : 1));
            return filteredTags;
        }

        return [];
    };

    @action onTagChange = async (tagValue: string, tag: InnerTag): Promise<void> => {
        const eventId = this.eventId();
        if (eventId === null) {
            console.error('onTagChange error, eventId is null');
            return;
        }

        const eventModel = eventId.getEventModel();
        if (eventModel === null) {
            console.error('onTagChange error, eventModel is null');
            return;
        }

        const eventTags = eventModel.getData().tags;
        eventTags[tag.key] = tagValue;
        const data: Record<string, Array<string>> = {};

        for (const [key, value] of Object.entries(eventTags)) {
            data[key] = [value];
        }

        await this.appState.sdkStaff.updateEvent({ event_id: eventId.toOldId(), requestBody: { tags: data } });
        await this.appState.sdkStaff.platformTags.refresh();
    };
}

type EventTagsPropsType = {
    eventId: EventId;
};

export const EventTags = observer('EventTags', (propsIn: EventTagsPropsType) => {
    const appState = useAppStateContext();
    const props = useAsObservableSource(propsIn);
    const [state] = useState(() => new EventTagsState(appState, () => props.eventId));
    const eventId = props.eventId;

    return (
        <div className='event-tags-form form'>
            <div className='row'>
                <div className='col-6 event-details-box'>
                    <table>
                        <thead>
                            <tr>
                                <th>Tag name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.getEventTags().map((tag) => {
                                return (
                                    <tr key={tag.key}>
                                        <td>{tag.label}</td>
                                        <td>
                                            <TagSelectElement
                                                tag={tag}
                                                eventId={eventId}
                                                eventTagState={state}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
});
